import {
  CasinoProviderListOut,
  CasinoGameListOut,
  CasinoSessionIn,
  CasinoSessionOut,
  AuthBundleOut,
  CatalogCollectionOut,
  CatalogHomeOut,
  LoginIn,
  PlaceBetIn,
  QuoteIn,
  QuoteOut,
  RegisterIn,
  SessionBootstrapOut,
  SportsCollectionOut,
  TournamentsCollectionOut,
  TicketOut,
  UserOut,
  WalletOut,
  WalletTransactionsOut,
  PaymentIn,
  PaymentOut,
  WithdrawalIn,
  PreferenceOut,
  PreferenceUpdateIn,
  NotificationOut,
  CashoutQuoteIn,
  CashoutQuoteOut,
  TopEventsCollectionOut,
  CustomBetAvailableSelectionsOut,
  CustomBetFilterOut,
  CustomBetFilterSelectionIn,
  TopMatchKombisOut,
  TopCombisOut,
  EventSnapshot,
  RegistrationOptionsOut,
  CategoriesCollectionOut,
  CustomerAccountOut,
  CustomerWalletOut,
  CasinoGame,
  CashbackSummaryOut,
  CashbackHistoryOut,
  ApiException
} from './types';

// In local dev (`npm start`) the base is relative ('') so every /v1 request — REST
// and the SSE stream — goes through the CRA dev-server proxy in src/setupProxy.js,
// which sidesteps the backend's missing CORS headers and self-signed cert.
// Production builds keep the absolute URL. Override anytime via REACT_APP_API_BASE.
export const BRAND_CODE = process.env.REACT_APP_BRAND_CODE || 'tip24';
export const BASE_URL =
  process.env.REACT_APP_API_BASE ??
  (process.env.NODE_ENV === 'development' ? '' : 'https://135.181.217.145');

function getToken(): string | null {
  return localStorage.getItem('access_token');
}

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

function onReAuthenticated(token: string) {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}

export function logout(): void {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  window.dispatchEvent(new Event('auth-changed'));
}

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getToken();
  const locale = localStorage.getItem('alex-bet-locale') || 'de';
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept-Language': locale,
    ...(options.headers as Record<string, string>),
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  let res = await fetch(`${BASE_URL}${path}`, { ...options, headers });

  if (res.status === 401 && token && path !== `/v2/brands/${BRAND_CODE}/auth/refresh`)  {
    const refreshToken = localStorage.getItem('refresh_token');
    if (refreshToken) {
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const refreshRes = await fetch(`${BASE_URL}/v2/brands/${BRAND_CODE}/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh_token: refreshToken }),
          });

          if (refreshRes.ok) {
            const data: AuthBundleOut = await refreshRes.json();
            localStorage.setItem('access_token', data.tokens.access_token);
            localStorage.setItem('refresh_token', data.tokens.refresh_token);
            onReAuthenticated(data.tokens.access_token);
          } else {
            logout();
            onReAuthenticated('');
          }
        } catch {
          logout();
          onReAuthenticated('');
        } finally {
          isRefreshing = false;
        }
      }

      const newToken = await new Promise<string>((resolve) => {
        refreshSubscribers.push((t) => resolve(t));
      });

      if (newToken) {
        headers['Authorization'] = `Bearer ${newToken}`;
        res = await fetch(`${BASE_URL}${path}`, { ...options, headers });
      }
    } else {
      logout();
    }
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    let errMsg = `HTTP ${res.status}`;
    if (err?.error?.message) {
      errMsg = err.error.message;
    } else if (err?.detail) {
      if (typeof err.detail === 'string') {
        errMsg = err.detail;
      } else if (err.detail.message) {
        errMsg = err.detail.message;
      }
    } else if (err?.message) {
      errMsg = err.message;
    }
    throw new ApiException(errMsg, res.status, err?.error, typeof err?.detail === 'string' ? err.detail : undefined);
  }

  return res.json() as Promise<T>;
}

// ── Catalog metadata cache ─────────────────────────────────────────────────────
// Transparent short-lived cache for idempotent catalog metadata GETs
// (sports/tournaments). It dedupes rapid identical requests — e.g. re-fetching
// the same tournament list while navigating between views — without changing any
// caller or the rendered UI. Abortable calls (options.signal) bypass the cache so
// one caller aborting can never reject a promise another caller is awaiting.
interface CatalogCacheEntry { ts: number; promise: Promise<unknown>; }
const catalogMetaCache = new Map<string, CatalogCacheEntry>();
const CATALOG_META_TTL_MS = 15000;

function cachedCatalogRequest<T>(
  path: string,
  options: RequestInit | undefined,
  fn: () => Promise<T>
): Promise<T> {
  if (options?.signal) return fn();
  const locale = localStorage.getItem('alex-bet-locale') || 'de';
  const key = `${locale}:${path}`;
  const now = Date.now();
  const hit = catalogMetaCache.get(key);
  if (hit && now - hit.ts < CATALOG_META_TTL_MS) return hit.promise as Promise<T>;
  const promise = fn().catch((err) => {
    catalogMetaCache.delete(key);
    throw err;
  });
  catalogMetaCache.set(key, { ts: now, promise });
  return promise;
}

// ── Auth ──────────────────────────────────────────────────────────────────────

export async function login(data: LoginIn): Promise<AuthBundleOut> {
  const result = await request<AuthBundleOut>(`/v2/brands/${BRAND_CODE}/auth/login`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  const accessToken = result.tokens?.access_token || (result as any).access_token;
  const refreshToken = result.tokens?.refresh_token || (result as any).refresh_token;
  if (accessToken) localStorage.setItem('access_token', accessToken);
  if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
  return result;
}

export async function getRegistrationOptions(): Promise<RegistrationOptionsOut> {
  return request<RegistrationOptionsOut>(`/v2/brands/${BRAND_CODE}/auth/registration-options`);
}

export async function register(data: RegisterIn): Promise<AuthBundleOut> {
  const result = await request<AuthBundleOut>(`/v2/brands/${BRAND_CODE}/auth/register`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  localStorage.setItem('access_token', result.tokens.access_token);
  localStorage.setItem('refresh_token', result.tokens.refresh_token);
  return result;
}

export async function getMe(): Promise<UserOut> {
  return request<UserOut>(`/v2/brands/${BRAND_CODE}/auth/me`);
}

export async function getBootstrap(): Promise<SessionBootstrapOut> {
  return request<SessionBootstrapOut>(`/v2/brands/${BRAND_CODE}/auth/bootstrap`);
}

export async function getPreferences(): Promise<PreferenceOut> {
  return request<PreferenceOut>(`/v2/brands/${BRAND_CODE}/auth/preferences`);
}

export async function updatePreferences(data: PreferenceUpdateIn): Promise<PreferenceOut> {
  return request<PreferenceOut>(`/v2/brands/${BRAND_CODE}/auth/preferences`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}



// ── Catalog ───────────────────────────────────────────────────────────────────

export async function getCatalogHome(params?: {
  live_limit?: number;
  pre_limit?: number;
  sport_id?: string;
  category_id?: string;
  tournament_id?: string;
  country_code?: string;
  pre_starts_after_hours?: number;
  pre_starts_before_hours?: number;
  window_anchor?: string;
  pre_within_hours?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}, options?: RequestInit): Promise<CatalogHomeOut> {
  const qs = new URLSearchParams();
  if (params?.live_limit !== undefined) qs.set('live_limit', String(params.live_limit));
  if (params?.pre_limit !== undefined) qs.set('pre_limit', String(params.pre_limit));
  if (params?.sport_id) qs.set('sport_id', params.sport_id);
  if (params?.category_id) qs.set('category_id', params.category_id);
  if (params?.tournament_id) qs.set('tournament_id', params.tournament_id);
  if (params?.country_code) qs.set('country_code', params.country_code);
  if (params?.pre_starts_after_hours !== undefined) qs.set('pre_starts_after_hours', String(params.pre_starts_after_hours));
  if (params?.pre_starts_before_hours !== undefined) qs.set('pre_starts_before_hours', String(params.pre_starts_before_hours));
  if (params?.window_anchor) qs.set('window_anchor', params.window_anchor);
  if (params?.pre_within_hours !== undefined) qs.set('pre_within_hours', String(params.pre_within_hours));
  if (params?.sort_by) qs.set('sort_by', params.sort_by);
  if (params?.sort_order) qs.set('sort_order', params.sort_order);
  const query = qs.toString() ? `?${qs}` : '';
  return request<CatalogHomeOut>(`/v2/brands/${BRAND_CODE}/catalog/home${query}`, options);
}

export async function getSports(params?: {
  scope?: string | null;
  within_hours?: number;
  starts_after_hours?: number;
  starts_before_hours?: number;
}, options?: RequestInit): Promise<SportsCollectionOut> {
  const qs = new URLSearchParams();
  if (params?.scope) qs.set('scope', params.scope);
  if (params?.within_hours !== undefined) qs.set('within_hours', String(params.within_hours));
  if (params?.starts_after_hours !== undefined) qs.set('starts_after_hours', String(params.starts_after_hours));
  if (params?.starts_before_hours !== undefined) qs.set('starts_before_hours', String(params.starts_before_hours));
  const query = qs.toString() ? `?${qs}` : '';
  const path = `/v2/brands/${BRAND_CODE}/catalog/sports${query}`;
  return cachedCatalogRequest(path, options, () => request<SportsCollectionOut>(path, options));
}

export async function getCategories(params?: {
  scope?: string | null;
  sport_id?: string | null;
  tournament_id?: string | null;
  country_code?: string | null;
  limit?: number;
  offset?: number;
  sort_by?: 'name';
  sort_order?: 'asc' | 'desc';
  starts_after_hours?: number;
  starts_before_hours?: number;
  window_anchor?: string;
}, options?: RequestInit): Promise<CategoriesCollectionOut> {
  const qs = new URLSearchParams();
  if (params?.scope) qs.set('scope', params.scope);
  if (params?.sport_id) qs.set('sport_id', params.sport_id);
  if (params?.tournament_id) qs.set('tournament_id', params.tournament_id);
  if (params?.country_code) qs.set('country_code', params.country_code);
  if (params?.limit !== undefined) qs.set('limit', String(params.limit));
  if (params?.tournament_id) qs.set('tournament_id', params.tournament_id);
  if (params?.sport_id) qs.set('sport_id', params.sport_id);
  if (params?.offset !== undefined) qs.set('offset', String(params.offset));
  if (params?.sort_by) qs.set('sort_by', params.sort_by);
  if (params?.sort_order) qs.set('sort_order', params.sort_order);
  if (params?.starts_after_hours !== undefined) qs.set('starts_after_hours', String(params.starts_after_hours));
  if (params?.starts_before_hours !== undefined) qs.set('starts_before_hours', String(params.starts_before_hours));
  if (params?.window_anchor) qs.set('window_anchor', params.window_anchor);
  const query = qs.toString() ? `?${qs}` : '';
  const path = `/v2/brands/${BRAND_CODE}/catalog/categories${query}`;
  return cachedCatalogRequest(path, options, () => request<CategoriesCollectionOut>(path, options));
}

export async function getTournaments(params?: {
  scope?: string | null;
  sport_id?: string | null;
  category_id?: string | null;
  country_code?: string | null;
  limit?: number;
  offset?: number;
  sort_by?: 'name' | 'event_count';
  sort_order?: 'asc' | 'desc';
  within_hours?: number;
  starts_after_hours?: number;
  starts_before_hours?: number;
}, options?: RequestInit): Promise<TournamentsCollectionOut> {
  const qs = new URLSearchParams();
  if (params?.scope) qs.set('scope', params.scope);
  if (params?.sport_id) qs.set('sport_id', params.sport_id);
  if (params?.category_id) qs.set('category_id', params.category_id);
  if (params?.country_code) qs.set('country_code', params.country_code);
  if (params?.limit !== undefined) qs.set('limit', String(params.limit));
  if (params?.offset !== undefined) qs.set('offset', String(params.offset));
  if (params?.sort_by) qs.set('sort_by', params.sort_by);
  if (params?.sort_order) qs.set('sort_order', params.sort_order);
  if (params?.within_hours !== undefined) qs.set('within_hours', String(params.within_hours));
  if (params?.starts_after_hours !== undefined) qs.set('starts_after_hours', String(params.starts_after_hours));
  if (params?.starts_before_hours !== undefined) qs.set('starts_before_hours', String(params.starts_before_hours));
  const query = qs.toString() ? `?${qs}` : '';
  const path = `/v2/brands/${BRAND_CODE}/catalog/tournaments${query}`;
  return cachedCatalogRequest(path, options, () => request<TournamentsCollectionOut>(path, options));
}

export async function getTopEvents(params?: {
  locale?: string;
  view?: 'home' | 'page';
  sport_id?: string;
  market_key?: string;
  limit?: number;
  offset?: number;
}, options?: RequestInit): Promise<TopEventsCollectionOut> {
  const qs = new URLSearchParams();
  if (params?.locale) qs.set('locale', params.locale);
  if (params?.view) qs.set('view', params.view);
  if (params?.sport_id) qs.set('sport_id', params.sport_id);
  if (params?.market_key) qs.set('market_key', params.market_key);
  if (params?.limit !== undefined) qs.set('limit', String(params.limit));
  if (params?.offset !== undefined) qs.set('offset', String(params.offset));
  const query = qs.toString() ? `?${qs}` : '';
  // Cached like sports/tournaments: several home sections request the same
  // top-events URL at mount, this collapses them into one network call.
  const path = `/v2/brands/${BRAND_CODE}/catalog/top-events${query}`;
  return cachedCatalogRequest(path, options, () => request<TopEventsCollectionOut>(path, options));
}

// ── Snapshots batch ───────────────────────────────────────────────────────────
// POST /v1/catalog/{scope}/snapshots — hydrate many events in ONE request
// instead of N single GET /catalog/events/{id} calls (docs §4).
export async function getSnapshotsBatch(
  scope: 'live' | 'pre' | 'outright',
  eventIds: string[],
  locale?: string,
  options?: RequestInit
): Promise<{ items: EventSnapshot[]; missing?: string[] }> {
  const qs = locale ? `?locale=${encodeURIComponent(locale)}` : '';
  return request(`/v2/brands/${BRAND_CODE}/catalog/${scope}/snapshots${qs}`, {
    ...options,
    method: 'POST',
    body: JSON.stringify({ event_ids: eventIds }),
  });
}

export async function getLiveEvents(options?: RequestInit): Promise<CatalogCollectionOut> {
  return request<CatalogCollectionOut>(`/v2/brands/${BRAND_CODE}/catalog/live/events`, options);
}


export async function getOutrightEvents(params?: {
  sport_id?: string;
  category_id?: string;
  country_code?: string;
  tournament_id?: string;
  limit?: number;
}, options?: RequestInit): Promise<CatalogCollectionOut> {
  const qs = new URLSearchParams();
  if (params?.limit !== undefined) qs.set('limit', String(params.limit));
  if (params?.sport_id) qs.set('sport_id', params.sport_id);
  if (params?.category_id) qs.set('category_id', params.category_id);
  if (params?.country_code) qs.set('country_code', params.country_code);
  if (params?.tournament_id) qs.set('tournament_id', params.tournament_id);
  const query = qs.toString() ? `?${qs}` : '';
  return request<CatalogCollectionOut>(`/v2/brands/${BRAND_CODE}/catalog/outright/events${query}`, options);
}

export async function getPreEvents(params?: {
  sport_id?: string;
  category_id?: string;
  country_code?: string;
  tournament_id?: string;
  within_hours?: number;
  starts_after_hours?: number;
  starts_before_hours?: number;
  limit?: number;
}, options?: RequestInit): Promise<CatalogCollectionOut> {
  const qs = new URLSearchParams();
  if (params?.limit !== undefined) qs.set('limit', String(params.limit));
  if (params?.sport_id) qs.set('sport_id', params.sport_id);
  if (params?.category_id) qs.set('category_id', params.category_id);
  if (params?.country_code) qs.set('country_code', params.country_code);
  if (params?.tournament_id) qs.set('tournament_id', params.tournament_id);
  if (params?.within_hours) qs.set('within_hours', String(params.within_hours));
  if (params?.starts_after_hours !== undefined) qs.set('starts_after_hours', String(params.starts_after_hours));
  if (params?.starts_before_hours !== undefined) qs.set('starts_before_hours', String(params.starts_before_hours));
  const query = qs.toString() ? `?${qs}` : '';
  return request<CatalogCollectionOut>(`/v2/brands/${BRAND_CODE}/catalog/pre/events${query}`, options);
}

export async function getEvent(eventId: string, scope?: string): Promise<any> {
  const query = scope ? `?scope=${encodeURIComponent(scope)}` : '';
  return request(`/v2/brands/${BRAND_CODE}/catalog/events/${encodeURIComponent(eventId)}${query}`);
}

// ── Bets ──────────────────────────────────────────────────────────────────────

export async function quoteBet(data: QuoteIn): Promise<QuoteOut> {
  return request<QuoteOut>(`/v2/brands/${BRAND_CODE}/bets/quote`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function placeBet(data: PlaceBetIn): Promise<TicketOut> {
  return request<TicketOut>(`/v2/brands/${BRAND_CODE}/bets/place`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}
export async function getBetHistory(
  limit: number = 50,
  dateFrom?: string,
  dateTo?: string,
  dateField: 'created_at' | 'settled_at' = 'created_at',
  offset: number = 0,
  timezone?: string,
  nameSource: 'placement' | 'current' = 'placement',
  locale?: string
): Promise<TicketOut[]> {
  const params = new URLSearchParams();
  params.append('limit', limit.toString());
  params.append('offset', offset.toString());
  if (dateFrom) params.append('date_from', dateFrom);
  if (dateTo) params.append('date_to', dateTo);
  params.append('date_field', dateField);
  
  const userTimezone = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  params.append('timezone', userTimezone);
  params.append('name_source', nameSource);
  if (locale) params.append('locale', locale);
  
  return request<TicketOut[]>(`/v2/brands/${BRAND_CODE}/bets/history?${params.toString()}`, {
    method: 'GET',
  });
}

export async function getBetByReference(reference: string, nameSource: 'placement' | 'current' = 'placement', locale?: string): Promise<TicketOut> {
  const qs = new URLSearchParams({ name_source: nameSource });
  if (locale) qs.set('locale', locale);
  return request<TicketOut>(`/v2/brands/${BRAND_CODE}/bets/by-reference/${encodeURIComponent(reference)}?${qs.toString()}`, {
    method: 'GET',
  });
}

export async function getTicket(ticketId: string, nameSource: 'placement' | 'current' = 'placement', locale?: string): Promise<TicketOut> {
  const qs = new URLSearchParams({ name_source: nameSource });
  if (locale) qs.set('locale', locale);
  return request<TicketOut>(`/v2/brands/${BRAND_CODE}/bets/${encodeURIComponent(ticketId)}?${qs.toString()}`);
}

export async function requestCashoutQuote(ticketId: string): Promise<CashoutQuoteOut> {
  return request<CashoutQuoteOut>(`/v2/brands/${BRAND_CODE}/cashout/quote`, {
    method: 'POST',
    body: JSON.stringify({ ticket_id: ticketId } as CashoutQuoteIn),
  });
}

export async function executeCashout(quoteId: string): Promise<TicketOut> {
  return request<TicketOut>(`/v2/brands/${BRAND_CODE}/cashout/execute/${quoteId}`, {
    method: 'POST',
  });
}

// ── Wallet ────────────────────────────────────────────────────────────────────

export async function getWallet(): Promise<WalletOut> {
  return request<WalletOut>(`/v2/brands/${BRAND_CODE}/wallet`);
}

export async function getCustomerAccount(): Promise<CustomerAccountOut> {
  return request<CustomerAccountOut>(`/v2/brands/${BRAND_CODE}/customer/account`);
}

export async function getCustomerWallet(): Promise<CustomerWalletOut> {
  return request<CustomerWalletOut>(`/v2/brands/${BRAND_CODE}/customer/wallet`);
}

export async function getWalletTransactions(limit: number = 50): Promise<WalletTransactionsOut> {
  return request<WalletTransactionsOut>(`/v2/brands/${BRAND_CODE}/wallet/transactions?limit=${limit}`);
}

export async function createDeposit(data: PaymentIn): Promise<PaymentOut> {
  return request<PaymentOut>(`/v2/brands/${BRAND_CODE}/wallet/deposits`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function createWithdrawal(data: WithdrawalIn): Promise<PaymentOut> {
  return request<PaymentOut>(`/v2/brands/${BRAND_CODE}/wallet/withdrawals`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ── Notifications ─────────────────────────────────────────────────────────────

export async function getNotifications(limit: number = 50): Promise<NotificationOut[]> {
  return request<NotificationOut[]>(`/v2/brands/${BRAND_CODE}/notifications?limit=${limit}`);
}

export async function markNotificationRead(notificationId: string): Promise<NotificationOut> {
  return request<NotificationOut>(`/v2/brands/${BRAND_CODE}/notifications/${encodeURIComponent(notificationId)}/read`, {
    method: 'POST',
  });
}

// ── CustomBet / Bet Builder ───────────────────────────────────────────────────
// Availability helpers for building same-event "all" (CustomBet) groups. These
// are UI hints only; /v1/bets/quote and /v1/bets/place remain authoritative.
// Both require bearer authentication.

export async function getCustomBetAvailableSelections(
  eventId: string,
  params?: { locale?: string; scope?: string },
  options?: RequestInit
): Promise<CustomBetAvailableSelectionsOut> {
  const qs = new URLSearchParams();
  if (params?.locale) qs.set('locale', params.locale);
  if (params?.scope) qs.set('scope', params.scope);
  const query = qs.toString() ? `?${qs}` : '';
  return request<CustomBetAvailableSelectionsOut>(
    `/v2/brands/${BRAND_CODE}/custombet/events/${encodeURIComponent(eventId)}/available-selections${query}`,
    options
  );
}

export async function filterCustomBetSelections(
  eventId: string,
  body: { scope?: string; selections: CustomBetFilterSelectionIn[] },
  params?: { locale?: string },
  options?: RequestInit
): Promise<CustomBetFilterOut> {
  const qs = new URLSearchParams();
  if (params?.locale) qs.set('locale', params.locale);
  const query = qs.toString() ? `?${qs}` : '';
  return request<CustomBetFilterOut>(
    `/v2/brands/${BRAND_CODE}/custombet/events/${encodeURIComponent(eventId)}/filter${query}`,
    { ...options, method: 'POST', body: JSON.stringify(body) }
  );
}

// ── Top MatchKombis ───────────────────────────────────────────────────────────
// Homepage prebuilt same-event CustomBet suggestion cards. No auth required.
export async function getTopMatchKombis(
  params?: { locale?: string; view?: string; limit?: number; tournament_id?: string; sport_id?: string },
  options?: RequestInit
): Promise<TopMatchKombisOut> {
  const qs = new URLSearchParams();
  if (params?.locale) qs.set('locale', params.locale);
  if (params?.view) qs.set('view', params.view);
  if (params?.limit !== undefined) qs.set('limit', String(params.limit));
  const query = qs.toString() ? `?${qs}` : '';
  return request<TopMatchKombisOut>(`/v2/brands/${BRAND_CODE}/catalog/top-match-kombis${query}`, options);
}

export async function getEventTopMatchKombis(
  eventId: string,
  params?: { view?: string; limit?: number; locale?: string },
  options?: RequestInit
): Promise<TopMatchKombisOut> {
  const qs = new URLSearchParams();
  if (params?.view) qs.set('view', params.view);
  if (params?.limit !== undefined) qs.set('limit', String(params.limit));
  if (params?.locale) qs.set('locale', params.locale);
  const query = qs.toString() ? `?${qs}` : '';
  return request<TopMatchKombisOut>(`/v2/brands/${BRAND_CODE}/catalog/events/${eventId}/top-match-kombis${query}`, options);
}

// ── Top Combis ────────────────────────────────────────────────────────────────
// Ready-made multi-event accumulator suggestions grouped by sport. No auth.
export async function getTopCombis(
  params?: {
    locale?: string;
    sport_ids?: string;
    limit_per_sport?: number;
    overall_limit?: number;
    folds?: string;
    source?: string;
    preview_legs?: string | number;
  },
  options?: RequestInit
): Promise<TopCombisOut> {
  const qs = new URLSearchParams();
  if (params?.locale) qs.set('locale', params.locale);
  if (params?.sport_ids) qs.set('sport_ids', params.sport_ids);
  if (params?.limit_per_sport !== undefined) qs.set('limit_per_sport', String(params.limit_per_sport));
  if (params?.overall_limit !== undefined) qs.set('overall_limit', String(params.overall_limit));
  if (params?.folds) qs.set('folds', params.folds);
  if (params?.source) qs.set('source', params.source);
  if (params?.preview_legs !== undefined) qs.set('preview_legs', String(params.preview_legs));
  const query = qs.toString() ? `?${qs}` : '';
  return request<TopCombisOut>(`/v2/brands/${BRAND_CODE}/catalog/top-combis${query}`, options);
}

export async function getTopCombiDetail(
  combiId: string,
  params?: { locale?: string },
  options?: RequestInit
): Promise<any> {
  const qs = new URLSearchParams();
  if (params?.locale) qs.set('locale', params.locale);
  const query = qs.toString() ? `?${qs}` : '';
  return request<any>(`/v2/brands/${BRAND_CODE}/catalog/top-combis/${encodeURIComponent(combiId)}${query}`, options);
}

// ── Casino ────────────────────────────────────────────────────────────────────

export async function getCasinoProviders(
  params?: { limit?: number; after?: string },
  options?: RequestInit
): Promise<CasinoProviderListOut> {
  const qs = new URLSearchParams();
  if (params?.limit !== undefined) qs.set('limit', String(params.limit));
  if (params?.after) qs.set('after', params.after);
  const query = qs.toString() ? `?${qs}` : '';
  return request<CasinoProviderListOut>(`/v2/brands/${BRAND_CODE}/casino/providers${query}`, options);
}

export async function getCasinoGames(
  params?: {
    provider_ref?: string;
    category?: string;
    device?: 'desktop' | 'mobile';
    search?: string;
    after?: string;
    limit?: number;
  },
  options?: RequestInit
): Promise<CasinoGameListOut> {
  const qs = new URLSearchParams();
  if (params?.provider_ref) qs.set('provider_ref', params.provider_ref);
  if (params?.category) qs.set('category', params.category);
  if (params?.device) qs.set('device', params.device);
  if (params?.search) qs.set('search', params.search);
  if (params?.after) qs.set('after', params.after);
  if (params?.limit !== undefined) qs.set('limit', String(params.limit));
  const query = qs.toString() ? `?${qs}` : '';
  return request<CasinoGameListOut>(`/v2/brands/${BRAND_CODE}/casino/games${query}`, options);
}

export async function getCasinoGameDetail(
  gameRef: string,
  options?: RequestInit
): Promise<CasinoGame> {
  return request<CasinoGame>(`/v2/brands/${BRAND_CODE}/casino/games/${gameRef}`, options);
}

export async function launchCasinoSession(data: CasinoSessionIn, options?: RequestInit): Promise<CasinoSessionOut> {
  return request<CasinoSessionOut>(`/v2/brands/${BRAND_CODE}/casino/sessions`, {
    ...options,
    method: 'POST',
    body: JSON.stringify(data),
    headers: {
      'Idempotency-Key': window.crypto.randomUUID(),
      'Origin': window.location.origin,
      ...(options?.headers || {}),
    },
  });
}


// ── Cashback ──────────────────────────────────────────────────────────────────

export async function getCashbackSummary(options?: RequestInit): Promise<CashbackSummaryOut> {
  return request<CashbackSummaryOut>(`/v2/brands/${BRAND_CODE}/cashback`, options);
}

export async function getCashbackHistory(
  params?: { limit?: number; before?: string },
  options?: RequestInit
): Promise<CashbackHistoryOut> {
  const qs = new URLSearchParams();
  if (params?.limit !== undefined) qs.set('limit', String(params.limit));
  if (params?.before) qs.set('before', params.before);
  const query = qs.toString() ? `?${qs}` : '';
  return request<CashbackHistoryOut>(`/v2/brands/${BRAND_CODE}/cashback/history${query}`, options);
}
